'use client';

import { useState, useMemo } from 'react';
import Link from 'next/link';
import * as XLSX from 'xlsx';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';

interface TeamMember {
  id: string;
  name: string;
  timezone: string;
  maxConsecutiveWeeks: number;
}

interface RotationWeek {
  weekNumber: number;
  startDate: string;
  endDate: string;
  primary: string;
  secondary: string;
}

const TIMEZONES = [
  'UTC-8 (PST)',
  'UTC-7 (MST)',
  'UTC-6 (CST)',
  'UTC-5 (EST)',
  'UTC+0 (GMT)',
  'UTC+1 (CET)',
  'UTC+5:30 (IST)',
  'UTC+8 (SGT)',
  'UTC+9 (JST)',
  'UTC+10 (AEST)',
];

export default function OnCallRotationOptimizer() {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([
    { id: '1', name: '', timezone: 'UTC-8 (PST)', maxConsecutiveWeeks: 2 },
  ]);
  const [rotationLength, setRotationLength] = useState(8);
  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    const monday = new Date(today);
    monday.setDate(today.getDate() - today.getDay() + 1);
    return monday.toISOString().split('T')[0];
  });

  // Command Center state
  const [saved, setSaved] = useState(false);

  // Command Center integration
  const {
    enabled: commandCenterEnabled,
    saving,
    error: saveError,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'oncall-rotation-optimizer',
    toolCategory: 'planner',
  });

  const addTeamMember = () => {
    if (teamMembers.length >= 5) {
      alert('Free tier limited to 5 team members. Upgrade to Pro for unlimited members.');
      return;
    }
    setTeamMembers([
      ...teamMembers,
      {
        id: Date.now().toString(),
        name: '',
        timezone: 'UTC-8 (PST)',
        maxConsecutiveWeeks: 2,
      },
    ]);
  };

  const removeTeamMember = (id: string) => {
    if (teamMembers.length === 1) return;
    setTeamMembers(teamMembers.filter((member) => member.id !== id));
  };

  const updateTeamMember = (id: string, field: keyof TeamMember, value: string | number) => {
    setTeamMembers(
      teamMembers.map((member) => (member.id === id ? { ...member, [field]: value } : member))
    );
  };

  const rotation = useMemo(() => {
    const validMembers = teamMembers.filter((m) => m.name.trim());
    if (validMembers.length === 0) return [];

    const weeks: RotationWeek[] = [];
    let primaryIndex = 0;
    let secondaryIndex = 1 % validMembers.length;

    for (let i = 0; i < rotationLength; i++) {
      const weekStart = new Date(startDate);
      weekStart.setDate(weekStart.getDate() + i * 7);

      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);

      weeks.push({
        weekNumber: i + 1,
        startDate: weekStart.toISOString().split('T')[0],
        endDate: weekEnd.toISOString().split('T')[0],
        primary: validMembers[primaryIndex].name,
        secondary: validMembers[secondaryIndex].name,
      });

      // Rotate to next members
      primaryIndex = (primaryIndex + 1) % validMembers.length;
      secondaryIndex = (secondaryIndex + 1) % validMembers.length;
    }

    return weeks;
  }, [teamMembers, rotationLength, startDate]);

  const fairnessAnalysis = useMemo(() => {
    const validMembers = teamMembers.filter((m) => m.name.trim());
    if (validMembers.length === 0 || rotation.length === 0) return null;

    const memberStats: Record<
      string,
      { primary: number; secondary: number; total: number; consecutive: number }
    > = {};

    validMembers.forEach((member) => {
      memberStats[member.name] = { primary: 0, secondary: 0, total: 0, consecutive: 0 };
    });

    let currentPrimary = '';
    let consecutiveCount = 0;

    rotation.forEach((week) => {
      memberStats[week.primary].primary++;
      memberStats[week.primary].total++;
      memberStats[week.secondary].secondary++;
      memberStats[week.secondary].total++;

      // Track consecutive weeks
      if (week.primary === currentPrimary) {
        consecutiveCount++;
        if (consecutiveCount > memberStats[week.primary].consecutive) {
          memberStats[week.primary].consecutive = consecutiveCount;
        }
      } else {
        currentPrimary = week.primary;
        consecutiveCount = 1;
      }
    });

    const totalWeeks = rotation.length;
    const fairShare = totalWeeks / validMembers.length;
    const maxDeviation = Math.max(
      ...Object.values(memberStats).map((stat) => Math.abs(stat.primary - fairShare))
    );

    return {
      memberStats,
      fairShare,
      maxDeviation,
      isFair: maxDeviation <= 1,
    };
  }, [teamMembers, rotation]);

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    if (!wb.Workbook) wb.Workbook = {};
    (wb.Workbook as any).CalcPr = { fullCalcOnLoad: true };

    // Schedule sheet
    const scheduleData = [
      ['On-Call Rotation Schedule'],
      ['Generated on:', new Date().toLocaleDateString()],
      ['Start Date:', startDate],
      ['Rotation Length:', `${rotationLength} weeks`],
      [],
      ['Week', 'Start Date', 'End Date', 'Primary On-Call', 'Secondary On-Call'],
      ...rotation.map((week) => [
        week.weekNumber,
        week.startDate,
        week.endDate,
        week.primary,
        week.secondary,
      ]),
    ];

    const scheduleSheet = XLSX.utils.aoa_to_sheet(scheduleData);
    XLSX.utils.book_append_sheet(wb, scheduleSheet, 'Schedule');

    // Team Members sheet
    const teamData = [
      ['Team Members'],
      [],
      ['Name', 'Timezone', 'Max Consecutive Weeks'],
      ...teamMembers
        .filter((m) => m.name.trim())
        .map((m) => [m.name, m.timezone, m.maxConsecutiveWeeks]),
    ];

    const teamSheet = XLSX.utils.aoa_to_sheet(teamData);
    XLSX.utils.book_append_sheet(wb, teamSheet, 'Team');

    // Fairness Analysis sheet
    if (fairnessAnalysis) {
      const fairnessData = [
        ['Fairness Analysis'],
        [],
        ['Team Member', 'Primary Weeks', 'Secondary Weeks', 'Total Weeks', 'Max Consecutive'],
        ...Object.entries(fairnessAnalysis.memberStats).map(([name, stats]) => [
          name,
          stats.primary,
          stats.secondary,
          stats.total,
          stats.consecutive,
        ]),
        [],
        ['Fair Share per Person:', fairnessAnalysis.fairShare.toFixed(1)],
        ['Maximum Deviation:', fairnessAnalysis.maxDeviation.toFixed(1)],
        ['Is Fair?', fairnessAnalysis.isFair ? 'Yes' : 'No'],
      ];

      const fairnessSheet = XLSX.utils.aoa_to_sheet(fairnessData);
      XLSX.utils.book_append_sheet(wb, fairnessSheet, 'Fairness');
    }

    XLSX.writeFile(wb, `oncall-rotation-${Date.now()}.xlsx`);
  };

  // Save to Command Center
  const handleSaveToCommandCenter = async () => {
    if (!commandCenterEnabled || rotation.length === 0) return;

    const validMembers = teamMembers.filter(m => m.name.trim());
    if (validMembers.length === 0) return;

    const memberScores: Record<string, number> = {};
    if (fairnessAnalysis) {
      Object.entries(fairnessAnalysis.memberStats).forEach(([name, stats]) => {
        memberScores[name] = stats.primary;
      });
    }

    const recommendations = [];
    if (fairnessAnalysis && !fairnessAnalysis.isFair) {
      recommendations.push({
        id: 'fairness-review',
        title: 'Review Distribution',
        priority: 'medium' as const,
        description: `Distribution deviation is ${fairnessAnalysis.maxDeviation.toFixed(1)} weeks from fair share. Consider adjusting rotation.`,
      });
    }
    if (validMembers.some(m => m.maxConsecutiveWeeks > 2)) {
      recommendations.push({
        id: 'consecutive-weeks',
        title: 'Monitor Consecutive Weeks',
        priority: 'low' as const,
        description: 'Some team members allow more than 2 consecutive weeks. Monitor for burn-out risk.',
      });
    }
    const timezones = new Set(validMembers.map(m => m.timezone));
    if (timezones.size > 1) {
      recommendations.push({
        id: 'timezone-optimization',
        title: 'Consider Time Zone Handoffs',
        priority: 'low' as const,
        description: `Team spans ${timezones.size} time zones. Consider optimizing handoff times for global coverage.`,
      });
    }

    const assessment = await saveResult({
      name: `On-Call Rotation - ${new Date().toLocaleDateString()}`,
      score_overall: fairnessAnalysis?.isFair ? 100 : Math.max(0, 100 - (fairnessAnalysis?.maxDeviation || 0) * 10),
      scores: memberScores,
      answers: {
        startDate,
        rotationLength,
        teamMembers: validMembers.map(m => ({ name: m.name, timezone: m.timezone, maxConsecutiveWeeks: m.maxConsecutiveWeeks })),
        rotation: rotation.map(r => ({ weekNumber: r.weekNumber, startDate: r.startDate, endDate: r.endDate, primary: r.primary, secondary: r.secondary })),
      },
      recommendations,
      metadata: {
        teamSize: validMembers.length,
        rotationWeeks: rotationLength,
        isFair: fairnessAnalysis?.isFair || false,
        fairShare: fairnessAnalysis?.fairShare || 0,
        maxDeviation: fairnessAnalysis?.maxDeviation || 0,
        timezoneCount: timezones.size,
      },
    });

    if (assessment) {
      setSaved(true);
    }
  };

  return (
    <div className="container mx-auto px-4 py-12 max-w-6xl">
      <div className="mb-8">
        <Link href="/tools" className="text-accent hover:text-accent transition-colors">
          ← Back to Tools
        </Link>
      </div>

      <div className="mb-12">
        <h1 className="text-4xl font-bold text-foreground mb-4">
          On-Call Rotation Optimizer
        </h1>
        <p className="text-xl text-foreground-secondary">
          Generate fair on-call schedules with burn-out risk analysis and time zone optimization
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Input Form */}
        <div className="space-y-6">
          {/* Rotation Settings */}
          <div className="bg-surface p-6 rounded-lg border border-border">
            <h2 className="text-2xl font-bold text-foreground mb-6">
              Rotation Settings
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Start Date (Monday)
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-4 py-2 bg-surface border border-border rounded text-foreground focus:border-accent focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Rotation Length (weeks)
                </label>
                <input
                  type="number" inputMode="numeric"
                  min="1"
                  max="52"
                  value={rotationLength}
                  onChange={(e) => setRotationLength(parseInt(e.target.value) || 8)}
                  className="w-full px-4 py-2 bg-surface border border-border rounded text-foreground focus:border-accent focus:outline-none"
                />
              </div>
            </div>
          </div>

          {/* Team Members */}
          <div className="bg-surface p-6 rounded-lg border border-border">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-foreground">Team Members</h2>
              <button
                onClick={addTeamMember}
                className="bg-accent text-accent-foreground px-4 py-2 rounded font-semibold hover:bg-accent-hover transition-colors text-sm"
              >
                + Add Member
              </button>
            </div>

            <div className="space-y-4">
              {teamMembers.map((member, index) => (
                <div
                  key={member.id}
                  className="p-4 bg-background rounded border border-border-muted"
                >
                  <div className="flex justify-between items-start mb-3">
                    <span className="text-sm font-semibold text-foreground">
                      Member {index + 1}
                    </span>
                    {teamMembers.length > 1 && (
                      <button
                        onClick={() => removeTeamMember(member.id)}
                        className="text-red-500 hover:text-red-400 text-sm"
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <div className="space-y-2">
                    <input
                      type="text"
                      placeholder="Name"
                      value={member.name}
                      onChange={(e) => updateTeamMember(member.id, 'name', e.target.value)}
                      className="w-full px-3 py-2 bg-surface border border-border rounded text-sm text-foreground placeholder:text-foreground-muted focus:border-accent focus:outline-none"
                    />

                    <select
                      value={member.timezone}
                      onChange={(e) => updateTeamMember(member.id, 'timezone', e.target.value)}
                      className="w-full px-3 py-2 bg-surface border border-border rounded text-sm text-foreground focus:border-accent focus:outline-none"
                    >
                      {TIMEZONES.map((tz) => (
                        <option key={tz} value={tz} className="bg-surface text-foreground">
                          {tz}
                        </option>
                      ))}
                    </select>

                    <div>
                      <label className="text-xs text-foreground-secondary">
                        Max Consecutive Weeks: {member.maxConsecutiveWeeks}
                      </label>
                      <input
                        type="range"
                        min="1"
                        max="4"
                        value={member.maxConsecutiveWeeks}
                        onChange={(e) =>
                          updateTeamMember(
                            member.id,
                            'maxConsecutiveWeeks',
                            parseInt(e.target.value)
                          )
                        }
                        className="w-full accent-orange"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {teamMembers.length >= 5 && (
              <p className="text-xs text-accent mt-2">
                Free tier limited to 5 members. Upgrade to Pro for unlimited team members.
              </p>
            )}
          </div>

          {/* Premium Features */}
          <div className="bg-accent-muted border border-border rounded-lg p-6">
            <h3 className="text-lg font-bold text-accent mb-3">🔒 Pro Features</h3>
            <ul className="space-y-2 text-sm text-foreground-secondary">
              <li>• Unlimited team members</li>
              <li>• Burn-out risk calculator</li>
              <li>• Time zone optimization</li>
              <li>• Holiday/vacation conflict detection</li>
              <li>• PagerDuty/Opsgenie integration</li>
            </ul>
            <Link
              href="/pricing"
              className="mt-4 block text-center bg-accent text-accent-foreground px-4 py-2 rounded font-semibold hover:bg-accent-hover transition-colors"
            >
              Upgrade to Pro
            </Link>
          </div>
        </div>

        {/* Results */}
        <div className="space-y-6">
          {/* Rotation Schedule */}
          <div className="bg-surface p-6 rounded-lg border border-border">
            <h2 className="text-2xl font-bold text-foreground mb-4">
              Rotation Schedule
            </h2>
            {rotation.length > 0 ? (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {rotation.map((week) => (
                  <div
                    key={week.weekNumber}
                    className="p-3 bg-background rounded border border-border-muted"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-sm font-bold text-accent">Week {week.weekNumber}</span>
                      <span className="text-xs text-foreground-tertiary">
                        {week.startDate} - {week.endDate}
                      </span>
                    </div>
                    <div className="space-y-1 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-foreground-secondary">
                          Primary:
                        </span>
                        <span className="font-semibold text-foreground">
                          {week.primary}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-foreground-secondary">
                          Secondary:
                        </span>
                        <span className="font-semibold text-foreground">
                          {week.secondary}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-foreground-tertiary text-center py-8">
                Add team members to generate rotation schedule
              </p>
            )}
          </div>

          {/* Fairness Analysis */}
          {fairnessAnalysis && (
            <div className="bg-surface p-6 rounded-lg border border-border">
              <h2 className="text-2xl font-bold text-foreground mb-4">
                Fairness Analysis
              </h2>
              <div className="space-y-3">
                {Object.entries(fairnessAnalysis.memberStats).map(([name, stats]) => (
                  <div
                    key={name}
                    className="p-3 bg-background rounded border border-border-muted"
                  >
                    <div className="font-semibold text-foreground mb-2">
                      {name}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs text-foreground-secondary">
                      <div>Primary: {stats.primary} weeks</div>
                      <div>Secondary: {stats.secondary} weeks</div>
                      <div>Total: {stats.total} weeks</div>
                      <div>Max Consecutive: {stats.consecutive}</div>
                    </div>
                  </div>
                ))}

                <div className="pt-4 border-t border-border">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-foreground-secondary">
                      Distribution Status:
                    </span>
                    <span
                      className={`font-semibold ${
                        fairnessAnalysis.isFair ? 'text-green-500' : 'text-accent'
                      }`}
                    >
                      {fairnessAnalysis.isFair ? '✓ Fair' : '⚠ Needs Review'}
                    </span>
                  </div>
                  <p className="text-xs text-foreground-tertiary mt-2">
                    Fair share: {fairnessAnalysis.fairShare.toFixed(1)} weeks per person
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Export Button */}
          <button
            onClick={exportToExcel}
            disabled={rotation.length === 0}
            className="w-full bg-accent text-accent-foreground px-6 py-3 rounded-lg font-semibold hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            📊 Export to Excel
          </button>

          {/* Command Center Save Button */}
          {rotation.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-4">
              {commandCenterEnabled && !saved && (
                <button
                  onClick={handleSaveToCommandCenter}
                  disabled={saving}
                  className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : 'Save to Command Center'}
                </button>
              )}
              {saved && (
                <div className="w-full px-6 py-3 bg-green-600/20 text-green-400 rounded-lg font-medium flex items-center justify-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Saved to Command Center
                </div>
              )}
              {saveError && (
                <div className="w-full px-6 py-3 bg-red-600/20 text-red-400 rounded-lg text-sm">
                  {saveError}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* How It Works */}
      <div className="mt-12 bg-surface p-8 rounded-lg border border-border">
        <h2 className="text-2xl font-bold text-foreground mb-4">How It Works</h2>
        <div className="grid md:grid-cols-2 gap-6 text-foreground-secondary">
          <div>
            <h3 className="font-semibold text-foreground mb-2">
              Fair Distribution
            </h3>
            <p className="text-sm">
              Rotation cycles through all team members evenly. Primary and secondary assignments
              are automatically balanced to prevent burn-out and ensure fair coverage.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-foreground mb-2">Consecutive Weeks</h3>
            <p className="text-sm">
              Set maximum consecutive weeks per person to prevent burn-out. The fairness analyzer
              tracks actual consecutive assignments and flags potential issues.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-foreground mb-2">Time Zones</h3>
            <p className="text-sm">
              Track team member time zones for distributed teams. Pro version optimizes handoffs
              and suggests ideal rotation timing based on global coverage needs.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-foreground mb-2">Export & Share</h3>
            <p className="text-sm">
              Export rotation schedules to Excel or iCal format. Team plan includes automatic sync
              with PagerDuty, Opsgenie, and calendar integrations.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
